<script setup lang="ts">
import { onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useVuelidate } from '@vuelidate/core'
import { required, numeric, between } from '@vuelidate/validators'
import { useUmsokn } from '@/composables/Applications/UseUmsokn'
import DatePicker from '../../Shared/DatePicker.vue'

const { t, locale } = useI18n()
const { umsokn } = useUmsokn()

const props = defineProps<{ disable?: boolean }>()

// Date menu state
const dateMenu = ref(false)

// Vuelidate rules
const rules = {
  umsokn: {
    taxStep: { required },
    taxDiscount: { between: between(0, 100), numeric },
    fromDate: { required },
  }
}

const v$ = useVuelidate(rules, { umsokn })

defineExpose({
  validate() {
    v$.value.$touch()
    return !v$.value.$invalid
  }
})

// Sanitize numeric input
watch(
  () => umsokn.value?.taxDiscount,
  (v) => {
    if (umsokn.value && v != null) umsokn.value.taxDiscount = String(v).replace(/[^\d]/g, '')
  }
)
</script>

<template>
  <div v-if="umsokn" class="col-sm-12">
    <v-row>
      <v-col>
        <h2 class="forms__header">
          {{ t('lifeyri_umsokn_step5_header1') }}
        </h2>
      </v-col>
    </v-row>

    <!-- Radio group for taxStep -->
    <v-row>
      <v-col>
        <v-radio-group
          v-model="umsokn.taxStep"
          :readonly="props.disable"
          hide-details
          :error="v$.umsokn.taxStep.$error"
          :error-messages="
            v$.umsokn.taxStep.$error ? [t('lifeyri_umsokn_step5_taxstep_error')] : []
          "
        >
          <v-radio :label="t('lifeyri_umsokn_step5_taxstep1')" :value="1" />
          <v-radio :label="t('lifeyri_umsokn_step5_taxstep2')" :value="2" />
          <v-radio :label="t('lifeyri_umsokn_step5_taxstep3')" :value="3" />
        </v-radio-group>
      </v-col>
    </v-row>

    <!-- Tax discount + From date -->
    <v-row>
      <v-col cols="12" md="6">
        <v-text-field
          v-model="umsokn.taxDiscount"
          :label="t('lifeyri_umsokn_step4_persontaxreduction')"
          variant="outlined"
          density="comfortable"
          inputmode="numeric"
          :readonly="props.disable"
          hide-details="auto"
          :error="v$.umsokn.taxDiscount.$error"
          :error-messages="
            v$.umsokn.taxDiscount.$error
              ? [
                  v$.umsokn.taxDiscount.between.$invalid
                    ? t('lifeyri_umsokn_step4_persontaxreduction_error_between')
                    : '',
                  v$.umsokn.taxDiscount.numeric.$invalid
                    ? t('lifeyri_umsokn_step4_persontaxreduction_error_numeric')
                    : ''
                ].filter(Boolean)
              : []
          "
        />
      </v-col>

      <v-col cols="12" md="6">
        <DatePicker
          v-model="umsokn.fromDate"
          :label="t('lifeyri_umsokn_step4_datefrom')"
          :disabled="props.disable"
          placeholder="DD.MM.YYYY"
          variant="outlined"
          density="comfortable"
          :error="v$.umsokn.fromDate.$error"
          :error-messages="v$.umsokn.fromDate.$error ? [t('lifeyri_umsokn_step4_datefrom_error')] : []"
        />
      </v-col>
    </v-row>

    <!-- Extra info + disclaimer -->
    <v-row>
      <v-col>
        <p class="forms__paragraph">
          {{ t('lifeyri_umsokn_step4_auka_upplysingar') }}
      </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <span class="forms__notice forms__notice-bold">
          {{ t('lifeyri_umsokn_header_fyrirvari') }}
        </span>
        <p class="forms__notice">
          {{ t('lifeyri_umsokn_step4_fyrirvari') }}
        </p>
      </v-col>
    </v-row>
  </div>
</template>
